import { onMounted, provide, reactive, toRefs } from 'vue';
import { library } from '@fortawesome/fontawesome-svg-core';
import { faRectangleXmark, faEllipsis } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';

library.add(faEllipsis, faRectangleXmark);

export default {
  name: 'TabsComponent',
  props: {
    cacheLifetime: {
      default: 5,
    },
    defaultTabHash: {
      type: String,
      required: false,
      default: '',
    },
    kolStyle: {
      type: Boolean,
      required: false,
      default: false,
    },
    windowLocation: {
      type: Boolean,
      required: false,
      default: true,
    },
    storeLocal: {
      type: Boolean,
      required: false,
      default: true,
    },
  },
  emits: ['changed', 'clicked'],
  components: { FontAwesomeIcon },
  setup(props, context) {
    const state = reactive({
      activeTabHash: '',
      lastActiveTabHash: '',
      showMobileTabs: false,
      tabs: []
    });

    provide('tabsProvider', state);
    provide('addTab', (tab) => {
      state.tabs.push(tab);
    });
    provide('updateTab', (computedId, data) => {
      let tabIndex = state.tabs.findIndex((tab) => tab.computedId === computedId);
      data.isActive = state.tabs[tabIndex].isActive;
      state.tabs[tabIndex] = data;
    });
    provide('deleteTab', (computedId) => {
      let tabIndex = state.tabs.findIndex((tab) => tab.computedId === computedId);
      state.tabs.splice(tabIndex, 1);
    });

    const setLocalStorage = (key, value, lifeTimeInMinutes) => {
      const currentTime = new Date().getTime();

      const expires = new Date(currentTime + lifeTimeInMinutes * 60000);

      localStorage.setItem(key, JSON.stringify({ value, expires }));
    };

    const getLocalStorage = (key) => {
      const cached = JSON.parse(
        localStorage.getItem(key)
      );
      if (!cached) {
        return null;
      }
      const expires = new Date(cached.expires);
      if (expires < new Date()) {
        localStorage.removeItem(key);
        return null;
      }

      return cached.value;
    };

    const toggleMobileTabs = (boolean) => {
      state.showMobileTabs = Boolean(boolean);
    };

    const selectTab = (selectedTabHash, event) => {
      if (event) {
        event.preventDefault();
      }
      const selectedTab = findTab(selectedTabHash);
      if (!selectedTab) {
        return;
      }
      if (selectedTab.isDisabled) {
        return;
      }
      if (state.lastActiveTabHash === selectedTab.hash) {
        context.emit('clicked', { tab: selectedTab });
        return;
      }
      state.tabs.forEach(tab => {
        tab.isActive = (tab.hash === selectedTab.hash);
      });
      context.emit('changed', { tab: selectedTab });
      state.lastActiveTabHash = state.activeTabHash = selectedTab.hash;
      if (props.storeLocal) {
        const storageKey = `koc-tabs-component.cache.${window.location.host}${window.location.pathname}`;
        setLocalStorage(storageKey, selectedTab.hash, props.cacheLifetime);
      }
      let yScroll = document.body.scrollTop;
      if (props.windowLocation) {
        window.location.hash = selectedTab.hash;
      }
      document.body.scrollTop = yScroll;
      state.showMobileTabs = false;
    };
    const findTab = (hash) => {
      return state.tabs.find(tab => tab.hash === hash);
    };

    onMounted(() => {
      if (!state.tabs.length) {
        return;
      }
      window.addEventListener('hashchange', () => selectTab(window.location.hash));
      if (findTab(window.location.hash)) {
        selectTab(window.location.hash);
        return;
      }
      if (props.storeLocal) {
        const storageKey = `koc-tabs-component.cache.${window.location.host}${window.location.pathname}`;
        const previousSelectedTabHash = getLocalStorage(storageKey);
        if (findTab(previousSelectedTabHash)) {
          selectTab(previousSelectedTabHash);
          return;
        }
      }
      if (props.defaultTabHash && findTab("#" + props.defaultTabHash)) {
        selectTab("#" + props.defaultTabHash);
        return;
      }

      selectTab(state.tabs[0].hash);
    });
    return {
      ...toRefs(state),
      selectTab,
      findTab,
      toggleMobileTabs
    };

  },
};
